import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useForm } from '@tanstack/react-form'
import { HugeiconsIcon } from '@hugeicons/react'
import { Target01Icon } from '@hugeicons/core-free-icons'
import { toast } from 'sonner'
import { z } from 'zod'
import type { AnyFieldApi } from '@tanstack/react-form'
import { useMutation } from 'convex/react'
import { api } from '@backend/_generated/api'
import type { Id } from '@backend/_generated/dataModel'

import { FieldInfo } from '@/components/common/field-info'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  usePositionRoleLevels,
  useTrajectoryPathManager,
} from '@/features/direct-reports/hooks'

interface AssignPositionFormData {
  positionRole: string
  positionLevel: string
  positionId: string
  path: string
}

const ASSIGN_POSITION_SCHEMA = z.object({
  positionRole: z.string().min(1, 'Position role is required'),
  positionLevel: z.string().min(1, 'Position level is required'),
  positionId: z.string().min(1, 'Position is required'),
  path: z.string().min(1, 'Trajectory path is required'),
})

type AssignPositionSearch = {
  orgId?: string
}

export const Route = createFileRoute('/onboarding/_blank/assign-position')({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>): AssignPositionSearch => {
    return {
      orgId: typeof search.orgId === 'string' ? search.orgId : undefined,
    }
  },
  beforeLoad: ({ search }) => {
    const pendingOrgId =
      search.orgId || localStorage.getItem('pendingOrganizationId')

    if (!pendingOrgId) {
      throw redirect({ to: '/onboarding' })
    }
  },
})

function RouteComponent() {
  const navigate = useNavigate()
  const joinAsAdmin = useMutation(api.organization.joinAsAdmin)
  const { uniqueRoles, getLevelsForRole, findPositionId } =
    usePositionRoleLevels()

  // Get organization ID from URL search params or localStorage
  const { orgId } = Route.useSearch()
  const organizationId =
    orgId || localStorage.getItem('pendingOrganizationId') || ''

  const form = useForm({
    defaultValues: {
      positionRole: '',
      positionLevel: '',
      positionId: '',
      path: '',
    },
    onSubmit: async ({ value }) => {
      try {
        if (!organizationId) {
          toast.error('Organization ID not found')
          return
        }

        await joinAsAdmin({
          organizationId: organizationId as Id<'organizations'>,
          positionId: value.positionId as Id<'positions'>,
          path: value.path,
        })

        // Clear pending organization data
        localStorage.removeItem('pendingOrganizationId')

        toast.success('Position assigned successfully!')

        // Convex reactivity will update the org list; full reload ensures
        // AuthSyncProvider picks up the new membership and sets currentOrganization.
        window.location.href = '/'
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : 'Failed to assign position',
        )
      }
    },
  })

  if (!organizationId) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Error</CardTitle>
            <CardDescription>
              Organization not found. Please create an organization first.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate({ to: '/onboarding' })}>
              Back to Create Organization
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <div className="w-full max-w-md space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center gap-2 mb-4">
            <HugeiconsIcon
              icon={Target01Icon}
              className="h-10 w-10 text-primary"
            />
          </div>
          <h1 className="text-3xl font-semibold tracking-tight">
            Select Your Position
          </h1>
          <p className="text-muted-foreground text-base">
            Choose your role and career path
          </p>
        </div>

        {/* Form Card */}
        <Card>
          <CardHeader>
            <CardTitle>Assign Position</CardTitle>
            <CardDescription>
              Select your starting position and career trajectory
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={(e) => {
                e.preventDefault()
                e.stopPropagation()
                form.handleSubmit()
              }}
              className="space-y-4"
            >
              {/* Hidden validators for programmatically set fields */}
              <form.Field
                name="positionId"
                validators={{
                  onDynamic: ASSIGN_POSITION_SCHEMA.shape.positionId,
                }}
                children={() => null}
              />

              {/* Position Role Field */}
              <form.Field
                name="positionRole"
                validators={{
                  onDynamic: ASSIGN_POSITION_SCHEMA.shape.positionRole,
                }}
              >
                {(field: AnyFieldApi) => (
                  <div className="space-y-2">
                    <Label htmlFor={field.name}>Position Role</Label>
                    <Select
                      value={field.state.value}
                      onValueChange={(value) => {
                        field.handleChange(value)
                        // Reset dependent fields
                        form.setFieldValue('positionLevel', '')
                        form.setFieldValue('positionId', '')
                        form.setFieldValue('path', '')
                      }}
                      disabled={form.state.isSubmitting}
                    >
                      <SelectTrigger id={field.name} className="w-full">
                        <SelectValue placeholder="Select a role" />
                      </SelectTrigger>
                      <SelectContent>
                        {uniqueRoles.map((role) => (
                          <SelectItem key={role} value={role}>
                            {role}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FieldInfo field={field} />
                  </div>
                )}
              </form.Field>

              {/* Position Level Field */}
              <form.Subscribe
                selector={(state: { values: AssignPositionFormData }) =>
                  state.values.positionRole
                }
              >
                {(selectedRole: string) => (
                  <form.Field
                    name="positionLevel"
                    validators={{
                      onDynamic: ASSIGN_POSITION_SCHEMA.shape.positionLevel,
                    }}
                  >
                    {(field: AnyFieldApi) => {
                      const levels = selectedRole
                        ? getLevelsForRole(selectedRole)
                        : []

                      return (
                        <div className="space-y-2">
                          <Label htmlFor={field.name}>Position Level</Label>
                          <Select
                            value={field.state.value}
                            onValueChange={(value) => {
                              field.handleChange(value)
                              // Derive positionId from role + level
                              if (selectedRole) {
                                const positionId = findPositionId(
                                  selectedRole,
                                  value,
                                )
                                if (positionId) {
                                  form.setFieldValue('positionId', positionId)
                                }
                              }
                            }}
                            disabled={!selectedRole || form.state.isSubmitting}
                          >
                            <SelectTrigger id={field.name} className="w-full">
                              <SelectValue placeholder="Select a level" />
                            </SelectTrigger>
                            <SelectContent>
                              {levels.map((levelOption) => (
                                <SelectItem
                                  key={levelOption.id}
                                  value={levelOption.level}
                                >
                                  {levelOption.level} - {levelOption.title}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FieldInfo field={field} />
                        </div>
                      )
                    }}
                  </form.Field>
                )}
              </form.Subscribe>

              {/* Trajectory Path Field */}
              <form.Subscribe
                selector={(state: { values: AssignPositionFormData }) =>
                  state.values.positionId
                }
              >
                {(selectedPositionId: string) => {
                  const {
                    trajectories,
                    isLoading: isLoadingTrajectories,
                    hasMultipleTrajectories,
                    singleTrajectory,
                    validateTrajectoryPath,
                  } = useTrajectoryPathManager({
                    positionId: selectedPositionId,
                    form,
                  })

                  return (
                    <form.Field
                      name="path"
                      validators={{
                        onDynamic: ({ value }: { value: string }) =>
                          validateTrajectoryPath(value),
                      }}
                    >
                      {(field: AnyFieldApi) => {
                        return (
                          <div className="space-y-2">
                            <Label htmlFor={field.name}>Career Path</Label>
                            {isLoadingTrajectories ? (
                              <div className="text-sm text-muted-foreground">
                                Loading career paths...
                              </div>
                            ) : !hasMultipleTrajectories && singleTrajectory ? (
                              <div className="p-3 bg-muted rounded-md">
                                <p className="text-sm font-medium">
                                  {singleTrajectory.path}
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">
                                  Default career path
                                </p>
                              </div>
                            ) : (
                              <>
                                <Select
                                  value={field.state.value}
                                  onValueChange={(value) => {
                                    field.handleChange(value)
                                  }}
                                  disabled={
                                    !selectedPositionId ||
                                    form.state.isSubmitting
                                  }
                                >
                                  <SelectTrigger
                                    id={field.name}
                                    className="w-full"
                                  >
                                    <SelectValue placeholder="Select a career path" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {trajectories.map((trajectory) => (
                                      <SelectItem
                                        key={trajectory.id}
                                        value={trajectory.path}
                                      >
                                        {trajectory.path}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <FieldInfo field={field} />
                              </>
                            )}
                          </div>
                        )
                      }}
                    </form.Field>
                  )
                }}
              </form.Subscribe>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate({ to: '/onboarding' })}
                  disabled={form.state.isSubmitting}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  disabled={form.state.isSubmitting}
                  className="flex-1"
                >
                  {form.state.isSubmitting
                    ? 'Completing Setup...'
                    : 'Complete Setup'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
